import React from 'react';
import { Button, Form, Input, Select } from 'antd';
import { COMPETENCIES, QuestionType, type CompetencySlug } from '@evaltick/shared';
import type { QuestionSetRef } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import styles from './QuestionForm.module.scss';

const { TextArea } = Input;

const COMPETENCY_OPTIONS = COMPETENCIES.map((c) => ({ label: c.name, value: c.id }));

const QUESTION_TYPE_OPTIONS = [
  { label: 'Behavioural', value: QuestionType.BEHAVIOURAL },
  { label: 'Situational', value: QuestionType.SITUATIONAL },
];

const DEFAULT_VALUES: QuestionFormValues = {
  competencyId: COMPETENCIES[0].id,
  questionType: QuestionType.BEHAVIOURAL,
  text: '',
};

export interface QuestionFormValues {
  competencyId: CompetencySlug;
  questionType: QuestionType;
  text: string;
}

interface QuestionFormProps {
  initialValues?: QuestionFormValues;
  /** Question sets that pin this question — shown as a non-blocking warning when editing. */
  affectedQuestionSets?: QuestionSetRef[];
  /** Present only when editing — renders the Created/Updated meta row. */
  meta?: { createdAt: string; updatedAt: string };
  onSubmit: (values: QuestionFormValues) => Promise<void>;
  /** Present only when editing — renders a Delete button alongside Save. */
  onDelete?: () => void;
  deleting?: boolean;
}

export const QuestionForm: React.FunctionComponent<QuestionFormProps> = ({
  initialValues,
  affectedQuestionSets = [],
  meta,
  onSubmit,
  onDelete,
  deleting,
}) => {
  const [form] = Form.useForm<QuestionFormValues>();
  const competencyId = Form.useWatch('competencyId', form);
  const questionType = Form.useWatch('questionType', form);
  const textValue = Form.useWatch('text', form);
  const canSave = Boolean(competencyId) && Boolean(questionType) && Boolean(textValue?.trim());

  const handleFinish = async (values: QuestionFormValues) => {
    await onSubmit(values);
    form.resetFields();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleFinish}
      initialValues={initialValues ?? DEFAULT_VALUES}
      className={styles.form}
    >
      {affectedQuestionSets.length > 0 && (
        <div className={styles.warnBox}>
          <strong>
            Used in {affectedQuestionSets.length} other question set
            {affectedQuestionSets.length === 1 ? '' : 's'}
          </strong>
          Saving changes will affect: {affectedQuestionSets.map((qs) => qs.name).join(', ')}.
        </div>
      )}
      <Form.Item
        name="competencyId"
        label="Competency *"
        rules={[{ required: true, message: 'Competency is required' }]}
      >
        <Select options={COMPETENCY_OPTIONS} />
      </Form.Item>
      <Form.Item
        name="questionType"
        label="Question Type *"
        rules={[{ required: true, message: 'Question type is required' }]}
      >
        <Select options={QUESTION_TYPE_OPTIONS} />
      </Form.Item>
      <Form.Item
        name="text"
        label="Question *"
        rules={[{ required: true, message: 'Question text is required' }]}
      >
        <TextArea autoSize={{ minRows: 4, maxRows: 8 }} placeholder="Write the question prompt…" />
      </Form.Item>
      {meta && (
        <div className={styles.metaRow}>
          <div className={styles.metaCol}>
            <span className={styles.metaLabel}>Created</span>
            <span className={styles.metaValue}>{formatDate(meta.createdAt)}</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaLabel}>Updated</span>
            <span className={styles.metaValue}>{formatDate(meta.updatedAt)}</span>
          </div>
        </div>
      )}
      <Form.Item className={styles.footer}>
        {onDelete && (
          <Button danger className={styles.btnDelete} loading={deleting} onClick={onDelete}>
            Delete
          </Button>
        )}
        <Button type="primary" htmlType="submit" className={styles.btnSave} disabled={!canSave}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
